<!-- /src/views/Dashboard/Product/List.vue  -->
<template>
    <div class="container-nzidoo">
        <page-title :title="isAdmin ? 'sidebar.productslist' : 'sidebar.myproducts'" />
        <!-- ======= FILTERS ======= -->
        <div class="filters scroll-horizontal">
            <button v-for="f in salesFilters" :key="f.key" class="btn-tab"
                :class="{ active: salesFilterStatus === f.key }" @click="changeSalesFilter(f.key)">
                {{ f.label }}
            </button>
        </div>
        <div class="col p-1 mb-2">
            <input v-model="searchQuery" type="text" :placeholder="$t('search_placeholder')"
                class="form-control search-input" />

        </div>
        <!-- Results Count -->
        <div class="results-info my-2 ml-2">
            <div v-if="filteredSalesProducts.length > 0">
                {{ $t('found_products', { count: filteredSalesProducts.length }) }}
            </div>
            <p v-else class="no-results">{{ $t('no_results') }}</p>
        </div>
        <!-- ======= LIST ======= -->
        <div v-if="!loader" class="">
            <div class="tab-panel">
                <div v-if="filteredSalesProducts.length === 0" class="empty">
                    {{ $t('sentence.noProductInThisSection') }}
                </div>
                <div v-else class="row px-2 px-md-3" ref="salesProductList">
                    <div v-for="product in filteredSalesProducts" :key="product.id" class="col-md-4 p-1 px-0 px-md-1">
                        <ProductCard :pictures="product.pictures" :user="product.user" :product="product"
                            :offer="product.offers[0]" :order="product.orders" :showStats=true :showBoost=true
                            :statusLabel="getProductStatusLabel(product)" @show="navigateToProductManagement(product)"
                            @update="editProduct(product)" @pause="pauseProduct(product)"
                            @delete="confirmDeleteProduct(product)" />
                    </div>
                </div>
                <!-- ======= LOAD MORE SECTION ======= -->
                <div v-if="pagination.has_more" class="load-more-container" ref="loadMoreTrigger">
                    <div v-if="loadingMore" class="loading-more">
                        <div class="spinner-small"></div>
                        <span>{{ $t('pagination.loading') || 'Chargement...' }}</span>
                    </div>
                </div>
            </div>

        </div>
        <LoadingSpinner v-else />
        <!-- ======= MODALS ======= -->
        <RatingModal :ratingModel="ratingModal" :ratingModelId="ratingModalId" :modelToRates="modalToRate"
            :modelToRatesId="modalToRateId" />
        <v-dialog />

        <notifications class="custom-notifications mt-2" group="success" position="top center" />
        <notifications class="custom-notifications mt-2" group="error" position="top center" />
    </div>
</template>

<script>
import config from "@/config.js";
import RatingModal from "@/components/modals/RatingModal";
import ProductCard from "@/components/templates/ProductCard.vue";
import gsapAnimations from "@/services/gsapAnimations";
import AlertService from "@/services/alertService";
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import PageTitle from '@/components/templates/PageTitle.vue';


export default {
    name: "product-list",
    components: {
        RatingModal,
        ProductCard,
        LoadingSpinner,
        PageTitle,
    },

    data() {
        return {
            storageUrl: config.storageUrl,
            productsByUser: [],
            salesFilterStatus: "inProgress",
            ratingModal: "",
            modalToRate: "",
            ratingModalId: -1,
            modalToRateId: -1,
            loader: false,
            loadingMore: false,
            isAdmin: false,
            searchQuery: "",

            pagination: {
                offset: 0,
                limit: 30,
                total: 0,
                has_more: false,
            },
            observer: null,
        };
    },

    async mounted() {
        this.loader=true;
        this.$nextTick(() => {
            this.initAnimations();
        });
        await this.getProductsList();
        this.loader=false;

        // Setup intersection observer for auto-loading
        this.setupIntersectionObserver();
    },

    watch: {
        filteredSalesProducts(newVal) {
            if (newVal.length>0) {
                this.$nextTick(() => {
                    const container=this.$refs.salesProductList;
                    if (container) {
                        gsapAnimations.animateNewItems(container, ".product-card");
                    }
                });
            }
        },
    },

    computed: {
        salesFilters() {
            return [
                { key: "all", label: this.$t("filter.all") },
                { key: "pending", label: this.$t("filter.toValidate") },
                { key: "inProgress", label: this.$t("filter.inProgress") },
                { key: "completed", label: this.$t("filter.completed") },
                { key: "paused", label: this.$t("filter.paused") },
                // { key: "cancelled", label: this.$t("filter.cancelled")||"Annulées" },
            ];
        },
        purchaseFilters() {
            return this.salesFilters;
        },

        /* ----- Sales Filter Logic ----- */
        filteredSalesProducts() {
            // Backend now handles status filtering, only apply search here
            let filtered=Array.isArray(this.productsByUser)
                ? this.productsByUser.slice()
                :[];

            // 🔍 Apply search filter only (case-insensitive)
            if (this.searchQuery.trim()!=="") {
                const q=this.searchQuery.toLowerCase();
                filtered=filtered.filter(
                    (p) =>
                        (p.name&&p.name.toLowerCase().includes(q))||
                        (p.description&&p.description.toLowerCase().includes(q))||
                        (p.fixed_price&&String(p.fixed_price).toLowerCase().includes(q))||
                        (p.starting_price&&String(p.starting_price).toLowerCase().includes(q))||
                        (p.user?.username&&p.user.username.toLowerCase().includes(q))
                );
            }

            return filtered;
        },

    },

    methods: {
        initAnimations() {
            // Animate current tab's products
            this.$nextTick(() => {
                if (this.filteredSalesProducts.length>0) {
                    const container=this.$refs.salesProductList;
                    if (container) {
                        gsapAnimations.setupScrollAnimation(container, ".product-card");
                    }
                }
            });
        },

        setupIntersectionObserver() {
            // Cleanup existing observer
            if (this.observer) {
                this.observer.disconnect();
                this.observer=null;
            }

            // ✅ Only setup observer if there are products and potential for more
            if (!this.pagination.has_more) {
                return;
            }

            // Create new observer
            this.observer=new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting&&this.pagination.has_more&&!this.loadingMore) {
                            this.loadMore();
                        }
                    });
                },
                {
                    root: null,
                    rootMargin: '100px',
                    threshold: 0.1,
                }
            );

            // Observe the load more trigger
            this.$nextTick(() => {
                const trigger=this.$refs.loadMoreTrigger;
                if (trigger) {
                    this.observer.observe(trigger);
                }
            });
        },

        async getProductsList(append=false) {
            try {
                const { data }=await this.$axios.get("products", {
                    params: {
                        filter: this.salesFilterStatus,
                    }
                });

                if (append) {
                    this.productsByUser=[...this.productsByUser, ...(data.products||[])];
                } else {
                    this.productsByUser=data.products||[];
                }

                this.isAdmin=data.isAdmin||false;

                // ✅ Animate new items if appending
                if (append&&data.products&&data.products.length>0) {
                    this.$nextTick(() => {
                        const container=this.$refs.salesProductList;
                        if (container) {
                            gsapAnimations.animateNewItems(container, ".product-card");
                        }
                    });
                }

                // ✅ Re-setup observer after content loads
                this.$nextTick(() => {
                    this.setupIntersectionObserver();
                });
            } catch (err) {
                this.$notify({
                    group: "error",
                    title: "Erreur",
                    text: this.$t('sentence.fetchError')||"Erreur lors du chargement des produits.",
                    type: "error",
                });
                console.error("Error fetching user data:", err);

            }
        },

        async changeSalesFilter(filterKey) {
            this.salesFilterStatus=filterKey;

            // ✅ Disconnect observer before resetting
            if (this.observer) {
                this.observer.disconnect();
                this.observer=null;
            }

            // Reset pagination when filter changes
            this.pagination={
                offset: 0,
                limit: this.pagination.limit,
                total: 0,
                has_more: false,
            };

            this.productsByUser=[];
            this.loader=true;

            await this.getProductsList(false);

            this.loader=false;

            // ✅ Trigger animations and reconnect observer
            this.$nextTick(() => {
                const container=this.$refs.salesProductList;
                if (container&&this.filteredSalesProducts.length>0) {
                    gsapAnimations.animateNewItems(container, ".product-card");
                }
                // Observer will be set up by getProductsList
            });
        },

        async loadMore() {
            if (this.loadingMore||!this.pagination.has_more) return;

            this.loadingMore=true;
            this.pagination.offset=Number(this.pagination.offset)+Number(this.pagination.limit);

            await this.getProductsList(true);

            this.loadingMore=false;
        },

        getProductStatusLabel(p) {
            if (!p) return this.$t("filter.unknown")||"Inconnu";
            if (Number(p.refused)===1) return this.$t("filter.refused")||"Refusé";
            if (p.validated===null)
                return this.$t("filter.inValidation")||"En validation";
            if (Number(p.has_been_sold)===1)
                return this.$t("filter.productSold")||"Vendu";
            if (p.sales_type==="auction") {
                if (Number(p.auction_status)===0)
                    return this.$t("filter.auctionEnded")||"Enchère terminée";
                return this.$t("filter.auctionActive")||"Enchère active";
            }
            return this.$t("filter.available")||"Disponible";
        },

        navigateToProductManagement(product) {
            if (product.status!=='non_validated') {

                this.$router.push({
                    name: 'ProductManagement',
                    params: { id: product.id }
                });
            }
        },

        async editProduct(product) {
            this.$router.push({
                name: 'ProductEdit',
                params: { id: product.id }
            });
        },

        async pauseProduct(product) {
            try {
                const confirmed=await AlertService.confirm({
                    title: product.paused
                        ? this.$t('sentence.confirmUnpause')||'Relancer ce produit ?'
                        :this.$t('sentence.confirmPause')||'Mettre ce produit en pause ?',
                });

                if (!confirmed) return;

                const { data }=await this.$axios.post(`product/toggle-pause/${product.id}`);

                if (data.success) {
                    product.paused=data.data.paused;

                    // Refresh the current page
                    this.pagination.offset=0;
                    this.productsByUser=[];
                    await this.getProductsList(false);

                    this.$notify({
                        group: "success",
                        text: product.paused!=null
                            ? this.$t('sentence.productPaused')
                            :this.$t('sentence.productUnpaused'),
                        type: "success",
                    });
                } else {
                    this.$notify({
                        group: "error",
                        title: "Erreur",
                        text: this.$t('sentence.actionFailed'),
                        type: "error",
                    });

                }
            } catch (error) {
                console.error("Error toggling pause:", error);
                this.$notify({
                    group: "error",
                    title: "Erreur",
                    text: this.$t('sentence.serverError')||"Erreur lors de l'opération.",
                    type: "error",
                });
            }
        },

        async confirmDeleteProduct(product) {
            const confirmed=await AlertService.confirm({
                title: this.$t('sentence.confirmDelete')||'Êtes-vous sûr ?',
            });

            if (confirmed) {
                this.deleteProduct(product);
            } else {
                this.$notify({
                    group: "error",
                    title: "Error",
                    text: "Suppression annulée.",
                    type: "error",
                });
            }
        },

        async deleteProduct(product) {
            try {
                const response=await this.$axios.delete(`product/${product.id}`);

                if (response?.data?.success) {
                    const index=this.productsByUser.findIndex(p => p.id===product.id);
                    if (index!==-1) {
                        this.productsByUser.splice(index, 1);
                    }

                    // If no products left and we're not on first page, reload from beginning
                    if (this.productsByUser.length===0&&this.pagination.offset>0) {
                        this.pagination.offset=0;
                        await this.getProductsList(false);
                    }

                    this.$notify({
                        group: "success",
                        title: "success",
                        text: this.$t('sentence.productDeleted')||"Le produit a été supprimé avec succès.",
                        type: "success",
                    });

                } else {
                    this.$notify({
                        group: "error",
                        title: "Erreur",
                        text: this.$t('sentence.deleteError')||"Erreur lors de la suppression du produit.",
                        type: "error",
                    });
                }
            } catch (error) {
                console.error("Error deleting product:", error);
                this.$notify({
                    group: "error",
                    title: "Erreur",
                    text: this.$t('sentence.deleteError')||"Erreur lors de la suppression.",
                    type: "error",
                });
            }
        }
    },

    beforeDestroy() {
        // Cleanup: kill all scroll triggers when component is destroyed
        gsapAnimations.killAllScrollTriggers();

        // Cleanup intersection observer
        if (this.observer) {
            this.observer.disconnect();
            this.observer=null;
        }
    }
};
</script>

<style scoped>
.scroll-horizontal {
    overflow-x: auto;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
}

/* ======= PRODUCT LIST ======= */
.product-list {
    display: flex;
    gap: .5rem;

}



/* ======= LOAD MORE SECTION ======= */
.load-more-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 2rem;
    padding: 1rem;
}

.loading-more {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--color-6);
    font-size: 0.95rem;
}

.spinner-small {
    width: 20px;
    height: 20px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid var(--color-nz);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.end-message {
    text-align: center;
    padding: 2rem 0;
    color: #7a88a6;
    font-size: 0.9rem;
    font-style: italic;
}

/* ======= EMPTY STATE ======= */
.empty {
    text-align: center;
    padding: 40px 0;
    color: #7a88a6;
    font-size: 0.95rem;
}

/* ======= RESPONSIVE ======= */
@media (max-width: 640px) {
    .content {
        margin: 10px auto;
        padding: 14px;
    }

    .btn-tab {
        min-width: 18%;
        padding: 6px 8px;
    }

}

@media (max-width: 320px) {
    .btn-tab {
        min-width: 10%;
        padding: 6px 8px;
    }

}
</style>